import { ApolloClient, InMemoryCache } from "@apollo/client";
import { makeAuthLink } from "./apollo/makeAuthLink";
import { makeErrorLink } from "./apollo/makeErrorLink";
import { makeHttpLink } from "./apollo/makeHttpLink";
import { makeRehydrateDatesLink } from "./apollo/makeRehydrateDatesLink";
import { makeRetryLink } from "./apollo/makeRetryLink";
import { makeSplitLink } from "./apollo/makeSplitLink";
import { makeSubscriptionsLink } from "./apollo/makeSubscriptionsLink";
import { backendConnectionStore } from "./connection/backendConnectionStore";
import { getBackendUrl } from "./getBackendUrl";
import { translate } from "./i18n/TranslationBackendRegistry";
import { getGlobalNotifier } from "./notifications/notifier";
// import { getAuthToken } from "./util/keycloak";

export const initApolloClient = (
  getAuthToken: () => Promise<string | null>,
) => {
  return initApolloClient2(getAuthToken).apolloClient;
};

export const initApolloClient2 = (
  getAuthToken: () => Promise<string | null>,
) => {
  const { graphQlUrl, graphQlSubscriptionUrl } = getBackendUrl();

  const connectionKey = "ws-connection";
  type ToastState = "none" | "reconnecting" | "final";
  let toastState: ToastState = "none";

  const showReconnecting = () => {
    if (toastState === "final") return;
    if (toastState === "reconnecting") return;
    toastState = "reconnecting";
    getGlobalNotifier().notify(
      "warning",
      translate("framework.connection.lost", "Verbindung unterbrochen"),
      {
        key: connectionKey,
        persist: true,
        description: "Erneut verbinden…",
      },
    );
  };

  const showDefinitivelyLost = () => {
    if (toastState === "final") return;
    toastState = "final";
    getGlobalNotifier().notify(
      "error",
      translate("framework.connection.lost", "Verbindung unterbrochen"),
      {
        key: connectionKey,
        persist: true,
        bypassDedupe: true,
        description: translate(
          "framework.connection.unreachable",
          "Keine Verbindung zum Server möglich. Bitte Seite neu laden.",
        ),
      },
    );
  };

  // Assume we start in a connecting state until the WS link reports connected.
  backendConnectionStore.setConnecting();

  const wsLink = makeSubscriptionsLink(
    graphQlSubscriptionUrl,
    getAuthToken,
    10,
    5_000,
    {
      onDelayedConnection: () => {
        backendConnectionStore.setConnecting();
        // Avoid a second notification here. We'll show a single persistent
        // "Connection lost / Reconnecting…" toast from the closed/error handlers.
      },
      connecting: () => {
        backendConnectionStore.setConnecting();
      },
      connected: () => {
        backendConnectionStore.setConnected();
        toastState = "none";
        getGlobalNotifier().dismiss(connectionKey);
      },
      closed: () => {
        backendConnectionStore.setDisconnected();
        showReconnecting();
      },
      error: () => {
        backendConnectionStore.setDisconnected();
        showReconnecting();
      },
      onFatalError: (errorOrCloseEvent) => {
        backendConnectionStore.setFinalDisconnected();
        // This is called by graphql-ws when it gives up retrying (non-lazy).
        // Show a final, definitive message.
        console.error(
          "WebSocket fatal error (retry exhausted)",
          errorOrCloseEvent,
        );
        showDefinitivelyLost();
      },
    },
  );
  const apolloClient = new ApolloClient({
    queryDeduplication: true,
    defaultOptions: {
      mutate: {
        errorPolicy: "all",
      },
      query: {
        errorPolicy: "all",
      },
    },
    cache: new InMemoryCache(),
    link: makeSplitLink(
      [
        makeRetryLink(),
        makeRehydrateDatesLink(),
        makeAuthLink(getAuthToken),
        makeHttpLink(graphQlUrl),
      ],
      [makeErrorLink(), makeRehydrateDatesLink(), wsLink],
    ),
  });

  return { apolloClient, wsLink };
};
